import { AppProductitemsSlideover } from '#components'
import { z } from '#netzo/shared/utils/zod'
import type { DropdownItem } from '#ui/types'
import type { ComponentProps } from 'vue-component-type-helpers'

// types:

type ProductitemitemsSlideoverProps = ComponentProps<typeof AppProductitemsSlideover>

export type ProductitemsUtils = ReturnType<typeof useProductitemsUtils>

export const useProductitems = () => {
  const {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
  } = useRows<Productitem>('productitems')

  const slideoverOpenCreate = (props: AppProductitemsSlideoverProps) => {
    return new Promise<Productitem>((resolve, reject) => {
      const data = getDefaultProductitem(props.data)
      slideover.open(AppProductitemsSlideover, {
        title: 'Crear item',
        ...props,
        data,
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitCreate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'create',
        openNestedCallback: slideoverOpenCreate,
      })
    })
  }

  const slideoverOpenEdit = (props: AppProductitemsSlideoverProps) => {
    return new Promise<Productitem>((resolve, reject) => {
      const data = getDefaultProductitem(props.data)
      slideover.open(AppProductitemsSlideover, {
        title: 'Editar item',
        ...props,
        data,
        disabledFields: ['productId'],
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitUpdate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'edit',
        openNestedCallback: slideoverOpenEdit,
      })
    })
  }

  const schema = z.object({
    name: z.string(),
    fiscalData: fiscalDataSchema,
    productId: z.string(),
  })

  // actions:

  const getDropdownItems = (row: Productitem, refresh: () => Promise<void>, utils: ProductitemsUtils, dropdownItemsAction: DropdownItem[] = []): DropdownItem[][] => [
    [
      getDropdownItem('editOrView', 'productitems', row, refresh, utils),
    ],
    [
      getDropdownItem('copyId', 'productitems', row, refresh, utils),
    ],
    [
      getDropdownItem('notes', 'productitems', row, refresh, utils),
      getDropdownItem('files', 'productitems', row, refresh, utils),
    ],
    [
      ...dropdownItemsAction,
    ],
    [
      getDropdownItem('info', 'productitems', row, refresh, utils),
      {
        label: row.immutable ? 'Desbloquear' : 'Bloquear',
        icon: row.immutable ? 'i-mdi-lock-open-variant' : 'i-mdi-lock',
        disabled: row?.product?.immutable,
        click: async () => {
          await onSubmitUpdate({ ...row, immutable: !row.immutable })
          await refresh()
        },
      },
    ],
    [
      getDropdownItem('archive', 'productitems', row, refresh, utils),
      getDropdownItem('delete', 'productitems', row, refresh, utils),
    ],
  ].filter(items => items?.length)

  return {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
    slideoverOpenCreate,
    slideoverOpenEdit,
    schema,
    getDropdownItems,
  }
}

export const useProductitemsUtils = ({ $products }: {
  $products?: Awaited<ReturnType<typeof useFetch<Product[]>>>
} = {}) => {
  const options = {
    ...optionsProductitems,
    fiscalData: {
      satUnitKey: optionsSAT.productUnitId,
    },
    productId: computed(() => $products?.data.value.map(toOption)),
  }

  const maps = {
    fiscalData: {
      satUnitKey: toMapByKey(optionsSAT.productUnitId, 'value'),
    },
    product: {
      type: toMapByKey(optionsProducts.type, 'value'),
    },
    productId: computed(() => toMapByKey($products?.data.value, 'id')),
    productsByType: computed(() => toMapByGroupKey($products?.data.value, 'type')),
  }

  return { options, maps }
}
